import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { differenceInDays } from 'date-fns';
import { Building2, Lock, Zap, Sparkles, Search, AlertCircle } from 'lucide-react';
import {
  subscriptionService,
  type CompanySubscriptionRow,
  type SubscriptionTier,
} from '../../services/subscriptionService';
import { useLanguage } from '../../features/language/useLanguage';
import { SegmentedToggle } from '../../components/common/SegmentedToggle';
import { Pagination } from '../../components/common/Pagination';
import { usePaginationState } from '../../hooks/usePaginationState';
import { effectiveTier, tierSortOrder } from './components/companies/companyTier';
import { CompanyStatCard } from './components/companies/CompanyStatCard';
import { CompaniesTable } from './components/companies/CompaniesTable';
import { AdjustSubscriptionModal } from './components/companies/AdjustSubscriptionModal';

type TierFilter = 'all' | SubscriptionTier;

export default function CompaniesSubscriptionsSettings() {
  const { t } = useLanguage();
  const [adjusting, setAdjusting] = useState<CompanySubscriptionRow | null>(null);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<TierFilter>('all');

  const { page, size, offset, setPage, setSize, resetOnChange } = usePaginationState({ defaultSize: 25 });
  resetOnChange([tierFilter, search]);

  const list = useQuery({
    queryKey: ['admin', 'companies-subscriptions'],
    queryFn: subscriptionService.adminListCompanies,
  });

  const stats = useMemo(() => {
    const data = list.data ?? [];
    const now = new Date();
    let free = 0;
    let basic = 0;
    let plus = 0;
    let expiring = 0;
    for (const r of data) {
      const eff = effectiveTier(r, now);
      if (eff === 'plus') plus += 1;
      else if (eff === 'basic') basic += 1;
      else free += 1;
      if (eff !== 'free' && r.expires_at) {
        const days = differenceInDays(new Date(r.expires_at), now);
        if (days >= 0 && days <= 7) expiring += 1;
      }
    }
    return { total: data.length, free, basic, plus, expiring };
  }, [list.data]);

  const rows = useMemo(() => {
    const data = list.data ?? [];
    const now = new Date();
    const s = search.trim().toLowerCase();
    return data
      .filter((r) => {
        if (tierFilter !== 'all' && effectiveTier(r, now) !== tierFilter) return false;
        if (s && !r.company_name.toLowerCase().includes(s)) return false;
        return true;
      })
      .sort((a, b) => {
        const tierA = effectiveTier(a, now);
        const tierB = effectiveTier(b, now);
        const tierCmp = tierSortOrder[tierA] - tierSortOrder[tierB];
        if (tierCmp !== 0) return tierCmp;

        return a.company_name.localeCompare(b.company_name, undefined, { sensitivity: 'base' });
      });
  }, [list.data, tierFilter, search]);

  const total = rows.length;
  const paginatedRows = useMemo(() => rows.slice(offset, offset + size), [rows, offset, size]);

  const tierFilters: { key: TierFilter; label: string }[] = [
    { key: 'all', label: t('subscription.admin.tier_filter.all') },
    { key: 'free', label: t('subscription.admin.tier_filter.free') },
    { key: 'basic', label: t('subscription.admin.tier_filter.basic') },
    { key: 'plus', label: t('subscription.admin.tier_filter.plus') },
  ];

  return (
    <div className="space-y-6" data-test-id="companies-subscriptions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('settings.companies_subscriptions')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('subscription.admin.page_subtitle')}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <CompanyStatCard icon={<Building2 className="h-4 w-4" />} label={t('subscription.admin.stats.total')} value={stats.total} tone="neutral" />
        <CompanyStatCard icon={<Lock className="h-4 w-4" />} label={t('subscription.admin.stats.free')} value={stats.free} tone="neutral" />
        <CompanyStatCard icon={<Zap className="h-4 w-4" />} label={t('subscription.admin.stats.basic')} value={stats.basic} tone="info" />
        <CompanyStatCard icon={<Sparkles className="h-4 w-4" />} label={t('subscription.admin.stats.plus')} value={stats.plus} tone="primary" />
        <CompanyStatCard icon={<AlertCircle className="h-4 w-4" />} label={t('subscription.admin.stats.expiring')} value={stats.expiring} tone="warning" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
        <SegmentedToggle
          ariaLabel={t('subscription.admin.col.tier')}
          value={tierFilter}
          onChange={setTierFilter}
          minTabWidth="100px"
          testIdPrefix="company-tier-filter"
          items={tierFilters}
        />
        <div className="relative w-full lg:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('subscription.admin.search_placeholder')}
            data-test-id="company-search-input"
            className="w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {list.isLoading ? (
          <div className="px-6 py-12 text-center text-sm text-gray-500">{t('common.loading')}</div>
        ) : total === 0 ? (
          <div className="px-6 py-16 text-center">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 mb-3">
              <Building2 className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">{t('subscription.admin.empty')}</p>
          </div>
        ) : (
          <CompaniesTable rows={paginatedRows} onAdjust={setAdjusting} />
        )}
        <Pagination
          page={page}
          pageSize={size}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setSize}
          isLoading={list.isLoading}
        />
      </div>

      {adjusting && <AdjustSubscriptionModal row={adjusting} onClose={() => setAdjusting(null)} />}
    </div>
  );
}
